import RenderPipeline from "../renderpipeline";
import ShaderService from "../../shader/shader_service";
import Camera from "../../scene/camera";
import Mesh from "../../geometry/mesh";
import { makeStaticMeshDataFromPrimitive } from "../../geometry/meshdata_factory";
import ResourceService from "../../resource/resourceservice";
import Transform from "../transform";

export default class PapercraftStaticRenderPipeline extends RenderPipeline {
  label: string;
  shader_name: string;
  texture_name: string;
  mesh_name: string;

  camera_buffer: GPUBuffer | null = null;
  object_buffer: GPUBuffer | null = null;
  paper_buffer: GPUBuffer | null = null;
  bind_group: GPUBindGroup | null = null;
  texture_bind_group: GPUBindGroup | null = null;
  mesh: Mesh;

  constructor(
    key: string,
    shader_name: string,
    mesh_name: string,
    texture_name: string
  ) {
    super(key + "_" + "papercraft_static");
    this.label = key + "_" + "papercraft_static";
    this.shader_name = shader_name;
    this.mesh_name = mesh_name + ".glb";
    this.texture_name = texture_name + ".png";
    this.mesh = new Mesh();
  }

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService
  ): Promise<void> {
    const shader = shader_service.get_render(this.shader_name);

    this.pipeline = device.createRenderPipeline({
      layout: "auto",
      label: this.label,
      vertex: {
        module: shader,
        entryPoint: "vs_main",
        buffers: [
          {
            arrayStride: 32,
            attributes: [
              { shaderLocation: 0, offset: 0,  format: "float32x3" },
              { shaderLocation: 1, offset: 12, format: "float32x3" },
              { shaderLocation: 2, offset: 24, format: "float32x2" },
            ],
          },
        ],
      },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [
          {
            format,
            blend: {
              color: {
                operation: "add",
                srcFactor: "src-alpha",
                dstFactor: "one-minus-src-alpha",
              },
              alpha: {
                operation: "add",
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
              },
            },
          },
        ],
      },
      primitive: {
        topology: "triangle-list",
        cullMode: "none",
      },
      depthStencil: {
        format: "depth24plus",
        depthWriteEnabled: true,
        depthCompare: "less",
      },
    });

    this.camera_buffer = device.createBuffer({
      size: 80,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.object_buffer = device.createBuffer({
      size: 64,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.paper_buffer = device.createBuffer({
      size: 48,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.bind_group = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      label: this.label + " cam_obj_paper_bind_group",
      entries: [
        { binding: 0, resource: { buffer: this.camera_buffer } },
        { binding: 1, resource: { buffer: this.object_buffer } },
        { binding: 2, resource: { buffer: this.paper_buffer } },
      ],
    });

    const texture_resource = await resource_service.getTexture(this.texture_name);
    const texture_asset = texture_resource.value;

    this.texture_bind_group = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(1),
      label: this.label + " tex_bind_group",
      entries: [
        { binding: 0, resource: texture_asset.sampler },
        { binding: 1, resource: texture_asset.view },
      ],
    });

    const mesh_resource = await resource_service.getMesh(this.mesh_name);
    const model = mesh_resource.value;

    if (!model.meshes.length) {
      throw new Error(this.mesh_name + " contains no mesh primitives");
    }

    const primitive = model.meshes[0];
    const meshData = makeStaticMeshDataFromPrimitive(primitive);
    this.mesh.build(device, meshData);
  }
    prepare(device: GPUDevice, camera: Camera, transform: Transform): void {
    if (!this.camera_buffer || !this.object_buffer || !this.paper_buffer) return;

    const cameraData = new Float32Array(20);
    cameraData.set(camera.view_projection_matrix, 0);
    cameraData[16] = camera.eye[0];
    cameraData[17] = camera.eye[1];
    cameraData[18] = camera.eye[2];
    cameraData[19] = 0.0;

    device.queue.writeBuffer(
      this.camera_buffer,
      0,
      cameraData,
      0,
      cameraData.length
    );

    const paperData = new Float32Array([
      -0.20, 0.78, 0.42, 0.10,
       1.00, 0.985, 0.95, 0.075,
       0.14, 0.52, 0.92, 0.0,
    ]);

    device.queue.writeBuffer(
      this.paper_buffer,
      0,
      paperData,
      0,
      paperData.length
    );

    device.queue.writeBuffer(
      this.object_buffer,
      0,
      transform.model_matrix.buffer as ArrayBuffer,
      transform.model_matrix.byteOffset,
      transform.model_matrix.byteLength
    );
  }

  draw(pass: GPURenderPassEncoder): void {
    if (!this.pipeline || !this.bind_group || !this.texture_bind_group) return;

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bind_group);
    pass.setBindGroup(1, this.texture_bind_group);
    this.mesh.draw(pass);
  }
}